/**
 * Per-line subscription controls in the cart.
 *
 * Two states per line, both rendered by snippets/subscription-line.liquid:
 *   • one-time line  → a "Subscribe & save" promo; pressing it moves the line
 *                      onto the plan the snippet resolved (data-default-plan).
 *   • subscribed line → a dropdown of the frequencies the merchant offers, plus
 *                      a one-time option (unless the variant is subscription-only).
 *
 * Which frequencies appear, and which duplicate plan wins, is decided in Liquid
 * now — so this file only writes the change and swaps the rendered cart in. It
 * used to prune the options client-side behind a document-wide MutationObserver;
 * that observer ran on every DOM change on the page and is gone.
 */
(function () {
  if (window.subscriptionLinesLoaded) return;
  window.subscriptionLinesLoaded = true;

  const root = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');

  /** Sections the drawer knows how to swap — the cart drawer itself plus the header bubbles. */
  function sectionsToRender() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((section) => section.id);
    }
    return [];
  }

  /**
   * Swap the freshly rendered cart markup in, without letting the drawer slide
   * open on its own, then tell the rest of the theme the cart moved.
   */
  function applySections(parsed) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && parsed && parsed.sections && typeof drawer.renderContents === 'function') {
      const autoOpens = drawer.classList.contains('open-after-adding');
      if (autoOpens) drawer.classList.remove('open-after-adding');
      drawer.renderContents(parsed);
      if (autoOpens) setTimeout(() => drawer.classList.add('open-after-adding'), 0);
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'subscription-lines' });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'subscription-lines' } }));
  }

  /** The line's current quantity, read off its stepper so no extra cart fetch is needed. */
  function quantityOf(line) {
    const input =
      document.getElementById(`Drawer-quantity-${line}`) || document.getElementById(`Quantity-${line}`);
    const value = input ? parseInt(input.value, 10) : NaN;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  async function postJSON(url, body) {
    const response = await fetch(root() + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Could not update this item.');
    return data;
  }

  function setCartBusy(busy) {
    document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items').forEach((node) => {
      node.classList.toggle('is-cart-syncing', busy);
    });
  }
  window.setCartSyncing = setCartBusy;

  function setBusy(el, busy) {
    setCartBusy(busy);
    el.classList.toggle('is-busy', busy);
    const spinner = el.querySelector('[data-subscription-spinner]');
    if (spinner) spinner.hidden = !busy;
    el.querySelectorAll('select, button').forEach((node) => {
      node.disabled = busy;
    });
  }

  /**
   * Move one cart line onto a selling plan, or back to one-time when planId is
   * empty. Shopify keeps the line key when only the plan changes, so the line
   * stays where it is in the cart.
   */
  async function applyPlan(el, planId) {
    const line = parseInt(el.dataset.line, 10);
    if (!line) return;

    const quantity = quantityOf(line);
    const sections = sectionsToRender();
    setBusy(el, true);

    try {
      let parsed = await postJSON('cart/change.js', {
        line,
        quantity,
        selling_plan: planId ? Number(planId) : null,
        sections,
        sections_url: window.location.pathname,
      });

      // Clearing a plan is the one case Shopify can refuse quietly, so check the
      // line came back one-time and fall back to re-adding it if it did not.
      if (!planId) {
        const updated = (parsed.items || [])[line - 1];
        if (updated && updated.selling_plan_allocation) {
          const variantId = Number(el.dataset.variantId);
          await postJSON('cart/change.js', { line, quantity: 0 });
          parsed = await postJSON('cart/add.js', {
            items: [{ id: variantId, quantity }],
            sections,
            sections_url: window.location.pathname,
          });
        }
      }

      applySections(parsed);
      setCartBusy(false);
    } catch (error) {
      setBusy(el, false);
      const select = el.querySelector('[data-subscription-select]');
      if (select) select.value = el.dataset.currentPlan || '';
      console.error(error);
    }
  }

  /**
   * Note the capture phase and the stopPropagation.
   *
   * `cart-items` (assets/cart.js) listens for *any* change event that bubbles
   * up out of a cart line and treats it as a quantity change:
   *
   *   onChange(event) { this.updateQuantity(event.target.dataset.index, event.target.value, …) }
   *
   * Our frequency dropdown lives inside a cart line, so picking a frequency
   * also handed the cart a line of `undefined` and a quantity of the selling
   * plan id — one bad /cart/change.js, and the red "There was an error while
   * updating your bag" under the line. Catching the event on the way down and
   * stopping it there means the cart never sees it; the quantity inputs are
   * untouched because we only stop events from our own select.
   */
  document.addEventListener(
    'change',
    (event) => {
      const select = event.target.closest && event.target.closest('[data-subscription-select]');
      if (!select) return;
      event.stopPropagation();
      const el = select.closest('subscription-line');
      if (el) applyPlan(el, select.value);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-subscription-subscribe]');
    if (!button) return;
    const el = button.closest('subscription-line');
    if (!el || !el.dataset.defaultPlan) return;
    event.preventDefault();
    applyPlan(el, el.dataset.defaultPlan);
  });
})();
